import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { AuthUser, LoginRequest } from '@/types'
import { authService } from '@/services/authService'
import { setSupabaseSession, restoreSupabaseSession } from '@/lib/supabase'
import toast from 'react-hot-toast'

interface AuthState {
  user: AuthUser | null
  isLoading: boolean
  isAuthenticated: boolean
  
  // Actions
  login: (credentials: LoginRequest) => Promise<boolean>
  logout: () => void
  initialize: () => Promise<void>
  refreshToken: () => Promise<boolean>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isLoading: true,
      isAuthenticated: false,

      login: async (credentials: LoginRequest) => {
        try {
          set({ isLoading: true })
          
          const response = await authService.login(credentials)
          console.log('Login response:', response)
          
          if (response.success && response.data) {
            const { user, access_token, refresh_token } = response.data
            console.log('Login successful, user:', user)
            console.log('Tokens:', { access_token: !!access_token, refresh_token: !!refresh_token })
            
            // Store tokens in localStorage
            localStorage.setItem('access_token', access_token)
            localStorage.setItem('refresh_token', refresh_token)
            // Store Supabase auth token for direct table queries
            if (response.data.supabase_access_token) {
              localStorage.setItem('supabase_access_token', response.data.supabase_access_token)
            }
            if (response.data.supabase_refresh_token) {
              localStorage.setItem('supabase_refresh_token', response.data.supabase_refresh_token)
            }
            console.log('✅ Tokens saved to localStorage')
            console.log('Verify - access_token in localStorage:', !!localStorage.getItem('access_token'))
            
            // Set Supabase auth session for direct table queries
            if (response.data.supabase_access_token && response.data.supabase_refresh_token) {
              await setSupabaseSession(response.data.supabase_access_token, response.data.supabase_refresh_token)
            }
            
            // 立即更新状态
            set({
              user,
              isAuthenticated: true,
              isLoading: false,
            })
            
            console.log('State updated, user is now:', user)
            toast.success('登录成功')
            return true
          } else {
            console.log('Login failed:', response.message)
            toast.error(response.message || '登录失败')
            set({ isLoading: false })
            return false
          }
        } catch (error) {
          console.error('Login error:', error)
          toast.error('登录失败，请稍后重试')
          set({ isLoading: false })
          return false
        }
      },

      logout: () => {
        // Clear tokens
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        localStorage.removeItem('supabase_access_token')
        localStorage.removeItem('supabase_refresh_token')
        
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        })
        
        toast.success('已退出登录')
      },

      initialize: async () => {
        try {
          set({ isLoading: true })
          
          const token = localStorage.getItem('access_token')
          const user = localStorage.getItem('auth-storage') 
            ? JSON.parse(localStorage.getItem('auth-storage') || '{}').state?.user 
            : null
          
          if (token && user) {
            // 检查token是否过期
            try {
              // 解码JWT token (简单的base64解码，不验证签名)
              const tokenParts = token.split('.')
              if (tokenParts.length === 3) {
                const payload = JSON.parse(atob(tokenParts[1]))
                
                // 检查过期时间
                if (payload.exp && payload.exp < Math.floor(Date.now() / 1000)) {
                  console.log('Token已过期，清除认证信息')
                  toast.error('登录已过期，请重新登录')
                  get().logout()
                  // 跳转到登录页
                  window.location.href = '/auth/login'
                  return
                }
              }
            } catch (decodeError) {
              console.error('Token解码失败:', decodeError)
              get().logout()
              window.location.href = '/auth/login'
              return
            }
            
            set({
              user,
              isAuthenticated: true,
              isLoading: false,
            })
            
            // Restore Supabase auth session for direct table queries
            await restoreSupabaseSession()
          } else {
            set({ isLoading: false })
          }
        } catch (error) {
          console.error('Initialize auth error:', error)
          get().logout()
        } finally {
          set({ isLoading: false })
        }
      },

      refreshToken: async () => {
        // For admin, just logout and require re-login
        get().logout()
        return false
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)